import { RichTextInput } from '@/components/RichTextInput';
import { Button, Group, Paper, Text } from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { useMemo, useState } from 'react';
import { JSONContent } from '@tiptap/react';
import { useTimelineContext } from '@/hooks/useTimelineContext';

interface TimelineNoteEditInputProps {
  initialContent?: string;
  initialRichTextContent?: JSONContent;
  onSave: (note: string, richTextContent?: JSONContent) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function TimelineNoteEditInput({
  initialContent,
  initialRichTextContent,
  onSave,
  onCancel,
  isLoading,
}: TimelineNoteEditInputProps) {
  const [content, setContent] = useState(initialContent);
  const [richTextContent, setRichTextContent] = useState(initialRichTextContent);
  const { careTeamContext } = useTimelineContext();
  const { careTeamProviders } = careTeamContext ?? {};

  const hasChanges = useMemo(() => {
    if (initialRichTextContent && richTextContent) {
      return JSON.stringify(initialRichTextContent) !== JSON.stringify(richTextContent);
    }
    if (initialContent && content) {
      return initialContent !== content;
    }
    return false;
  }, [initialContent, content, initialRichTextContent, richTextContent]);

  const isEmpty = useMemo(() => {
    if (initialRichTextContent && richTextContent) {
      return JSON.stringify(richTextContent) === '{}';
    }
    if (initialContent && content) {
      return content.trim() === '';
    }
    return true;
  }, [initialContent, content, initialRichTextContent, richTextContent]);

  const handleSave = () => {
    onSave(content ?? '', richTextContent);
  };

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      bg="#fffbeb"
      pos="relative"
      mt="8px"
      style={{
        boxShadow: '0 4px 12px rgba(251, 191, 36, 0.15)',
        borderColor: '#FCECCC',
      }}
    >
      <Text variant="caption" mb="xs" fw={500}>
        Edit sticky note
      </Text>

      <RichTextInput
        value={initialRichTextContent ?? initialContent ?? ''}
        onChange={(text, json) => {
          setContent(text);
          setRichTextContent(json);
        }}
        mentionItems={(careTeamProviders ?? []).map((provider) => ({
          id: getReferenceString(provider) ?? '',
          label: formatHumanName(provider.name?.[0]),
        }))}
      />

      <Group justify="end" mt="sm">
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            color="gray"
            onClick={onCancel}
            disabled={isLoading}
            style={{ textDecoration: 'none', borderRadius: '100px' }}
          >
            Cancel
          </Button>
          <Button
            variant="subtle"
            size="xs"
            color="blue"
            onClick={handleSave}
            disabled={isEmpty || !hasChanges || isLoading}
            loading={isLoading}
            style={{ textDecoration: 'none', borderRadius: '100px' }}
          >
            Save
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}
